import React, { useEffect, useState } from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { Button, BUTTON_VARIANT, BUTTON_INTENT } from 'components/Button'
import ClientSignIn from 'components/ClientSignIn'
import ClientSignUp from 'components/ClientSignUp'
import { Flex } from 'components/Layout'
import Link from 'components/Link'
import { Modal, type ModalProps } from 'components/Modal'
import { Text } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import { useUserAuth } from 'providers/userAuth'
import { AUTHENTICATION_SCREEN_TYPE, USER_TYPE } from 'providers/userAuthUtil'
import { SPACE, MODAL_MAX_WIDTH, FONT_SIZE, BORDER_WIDTH, COLOR } from 'Theme'

const Divider = styled.div`
  border-top: ${BORDER_WIDTH.PX_1} solid ${COLOR.GRAYSCALE_5};
`

interface AuthModalProps extends Pick<ModalProps, 'onModalClose'> {
  userAuthType: (typeof USER_TYPE)[keyof typeof USER_TYPE]
  onSignInCompleted: () => void
  isModalOpen?: boolean
  isLoading?: boolean
}

export const AuthModal: React.FC<React.PWC<AuthModalProps>> = ({
  onModalClose,
  isModalOpen,
  onSignInCompleted,
  isLoading,
  userAuthType,
}) => {
  const userAuth = useUserAuth()
  const [isCallbackTriggered, setIsCallbackTriggered] = useState(false)
  const { user, isPageLoading } = userAuth
  const [authenticationScreenType, setAuthenticationScreenType] = useState(
    AUTHENTICATION_SCREEN_TYPE.SIGN_UP
  )

  useEffect(() => {
    setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_IN)
  }, [userAuth.isLoading])

  useEffect(() => {
    if (
      userAuthType === USER_TYPE.CLIENT &&
      userAuth.isClientSignedIn &&
      user?.id &&
      isModalOpen &&
      !isLoading &&
      !isCallbackTriggered
    ) {
      onSignInCompleted()
      onModalClose()
      setIsCallbackTriggered(true)
    }
  }, [
    isModalOpen,
    onModalClose,
    onSignInCompleted,
    user,
    userAuth.isClientSignedIn,
    isLoading,
    isCallbackTriggered,
    userAuthType,
  ])

  let modalContent = <WrappedLoader />
  let modalLabel = ''

  if (
    !user &&
    authenticationScreenType === AUTHENTICATION_SCREEN_TYPE.SIGN_UP
  ) {
    modalLabel = 'Sign up'
    modalContent = (
      <>
        <Flex p={SPACE.PX_30} justifyContent="center">
          <Flex maxWidth={MODAL_MAX_WIDTH.PX_350} flexDirection="column">
            <Flex mb={SPACE.PX_15} flexDirection="column">
              <ClientSignUp
                onSignUpCompleted={() => {
                  setAuthenticationScreenType(
                    AUTHENTICATION_SCREEN_TYPE.SIGN_IN
                  )
                }}
              />
            </Flex>
            <Button
              variant={BUTTON_VARIANT.MINIMAL}
              intent={BUTTON_INTENT.NONE}
              onClick={() => {
                setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_IN)
              }}
            >
              Already have an account? Sign in.
            </Button>
          </Flex>
        </Flex>
        <Divider />
        <Flex justifyContent="center">
          <Text
            mt={SPACE.PX_15}
            mb={SPACE.PX_10}
            textAlign="center"
            fontSize={FONT_SIZE.PX_12}
          >
            This site is protected by reCAPTCHA and the Google <br />
            <Link
              href="https://policies.google.com/privacy"
              target="__blank"
              fontSize={FONT_SIZE.PX_12}
              aria-label="Privacy Policy"
            >
              <Text fontSize={FONT_SIZE.PX_12}>&nbsp;Privacy Policy&nbsp;</Text>
            </Link>
            and
            <Link
              href="https://policies.google.com/terms"
              target="__blank"
              aria-label="Terms of Service"
            >
              <Text fontSize={FONT_SIZE.PX_12}>
                &nbsp;Terms of Service&nbsp;
              </Text>
            </Link>
            apply.
          </Text>
        </Flex>
      </>
    )
  }
  if (
    !user &&
    authenticationScreenType === AUTHENTICATION_SCREEN_TYPE.SIGN_IN
  ) {
    modalLabel = 'Sign in'
    modalContent = (
      <Flex p={SPACE.PX_30} justifyContent="center">
        <Flex maxWidth={MODAL_MAX_WIDTH.PX_350} flexDirection="column">
          <Flex mb={SPACE.PX_15} flexDirection="column">
            <ClientSignIn />
          </Flex>
          <Button
            variant={BUTTON_VARIANT.MINIMAL}
            intent={BUTTON_INTENT.NONE}
            onClick={() => {
              setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_UP)
            }}
          >
            Don&apos;t have an account? Sign up.
          </Button>
        </Flex>
      </Flex>
    )
  }
  if (isPageLoading) {
    modalContent = <WrappedLoader />
  }

  return (
    <Modal
      isWithFooter={false}
      isWithoutPaddings
      ariaLabel={modalLabel}
      isOpen={isModalOpen}
      onModalClose={onModalClose}
      title={modalLabel}
      width="100%"
      maxWidth={MODAL_MAX_WIDTH.PX_400}
      data-test-id={DATA_TEST_ID.AUTH_MODAL}
    >
      {modalContent}
    </Modal>
  )
}
